/**
 * Web Push subscription helper.
 *
 * Flow:
 *   1. Fetch the VAPID public key from /api/config (cached in module scope).
 *   2. Check whether the browser already has a subscription — if yes,
 *      just sync it with the backend and return.
 *   3. Ask for Notification permission (only on explicit user action,
 *      e.g. the in-app prompt button — never automatic on page load).
 *   4. Call PushManager.subscribe with the VAPID key.
 *   5. POST the subscription JSON to /api/notifications/subscribe.
 *
 * Dismissal is persisted under `pcc.pushPrompt.dismissed` so the in-app
 * banner doesn't reappear every navigation. Re-enable via DevTools by
 * removing that localStorage key.
 */
import api from '../services/api';

const DISMISS_KEY = 'pcc.pushPrompt.dismissed';
const SUBSCRIBED_KEY = 'pcc.pushSubscribed';

let _vapidPublicKey = null;
let _vapidFetchPromise = null;

/** Web Push expects the VAPID public key as a Uint8Array, not base64. */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const fetchVapidPublicKey = async () => {
  if (_vapidPublicKey !== null) return _vapidPublicKey;
  if (_vapidFetchPromise) return _vapidFetchPromise;
  _vapidFetchPromise = api
    .get('/config')
    .then((res) => {
      _vapidPublicKey = res.data?.vapidPublicKey || '';
      return _vapidPublicKey;
    })
    .catch(() => {
      _vapidPublicKey = '';
      return '';
    });
  return _vapidFetchPromise;
};

/**
 * Capabilities check — what's possible in this browser RIGHT NOW.
 * Returns an object describing every gate that has to clear for push
 * to be usable, so the caller can render the right UI hint.
 */
export const getPushCapabilities = () => {
  const hasSW = 'serviceWorker' in navigator;
  const hasPush = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  // iOS detection — Web Push on iOS only works in an installed PWA
  // (iOS 16.4+). Standalone mode is the signal that the user added
  // the app to the home screen.
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.navigator.standalone === true;
  const permission = hasNotification ? Notification.permission : 'unsupported';

  return {
    supported: hasSW && hasPush && hasNotification,
    permission, // 'default' | 'granted' | 'denied' | 'unsupported'
    isIos,
    isStandalone,
    iosNeedsPwa: isIos && !isStandalone,
    dismissed: localStorage.getItem(DISMISS_KEY) === '1',
    subscribed: localStorage.getItem(SUBSCRIBED_KEY) === '1',
  };
};

/** Mark the in-app banner as dismissed so we don't keep nagging. */
export const dismissPushPrompt = () => {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
};

/** Reset the dismissal so the next eligible login re-shows the banner. */
export const resetPushPrompt = () => {
  try { localStorage.removeItem(DISMISS_KEY); } catch (_) {}
};

/**
 * Request permission + subscribe + POST to backend. Returns true on
 * success, false on any failure (with a console.warn for diagnostics).
 *
 * MUST be called from a user gesture (click handler) — browsers will
 * reject Notification.requestPermission() otherwise.
 */
export const enablePushNotifications = async () => {
  const caps = getPushCapabilities();
  if (!caps.supported) {
    console.warn('[push] Browser does not support Web Push');
    return false;
  }
  if (caps.iosNeedsPwa) {
    console.warn('[push] iOS Safari requires installed PWA for Web Push');
    return false;
  }

  // 1. Permission
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    dismissPushPrompt(); // user said no — respect that
    return false;
  }

  // 2. Service worker registration (registered by index.html, but we
  // also wait for it just in case)
  const reg = await navigator.serviceWorker.ready;
  if (!reg) {
    console.warn('[push] Service worker not ready');
    return false;
  }

  // 3. VAPID key from backend
  const vapidKey = await fetchVapidPublicKey();
  if (!vapidKey) {
    console.warn('[push] VAPID public key not configured on backend');
    return false;
  }

  // 4. Subscribe (reuses existing subscription if any)
  let subscription;
  try {
    subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }
  } catch (e) {
    console.warn('[push] PushManager.subscribe failed:', e);
    return false;
  }

  // 5. Send to backend
  try {
    const sub = subscription.toJSON();
    await api.post('/notifications/subscribe', {
      endpoint: sub.endpoint,
      keys: sub.keys,
      expirationTime: sub.expirationTime,
      userAgent: navigator.userAgent,
    });
    try { localStorage.setItem(SUBSCRIBED_KEY, '1'); } catch (_) {}
    return true;
  } catch (e) {
    console.warn('[push] Backend subscribe failed:', e);
    return false;
  }
};

/**
 * Silent resync — if the browser already holds a push subscription,
 * re-POST it to the backend. Heals cases where localStorage says
 * "subscribed" but the backend record is missing (server restart,
 * DB reset, or the initial POST was blocked by network). Never asks
 * for permission — safe to call outside a user gesture.
 */
export const resyncPushSubscription = async () => {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    if (Notification?.permission !== 'granted') return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return false;
    const sub = subscription.toJSON();
    await api.post('/notifications/subscribe', {
      endpoint: sub.endpoint,
      keys: sub.keys,
      expirationTime: sub.expirationTime,
      userAgent: navigator.userAgent,
    });
    try { localStorage.setItem(SUBSCRIBED_KEY, '1'); } catch (_) {}
    return true;
  } catch (e) {
    console.warn('[push] resync failed:', e);
    return false;
  }
};


/** Remove the subscription on logout. Best-effort — failures are silent. */
export const disablePushNotifications = async () => {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try {
        await api.delete(`/notifications/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`);
      } catch (_) {}
      await sub.unsubscribe();
    }
    try {
      localStorage.removeItem(SUBSCRIBED_KEY);
      localStorage.removeItem(DISMISS_KEY);
    } catch (_) {}
  } catch (e) {
    console.warn('[push] disable failed:', e);
  }
};
